import { CheckIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { Button, DocumentsList, GarantiesList, PricingCard, SmartButton, TarifRecap, Commission } from '@workspace/core-ts/components';
import type { CommissionConfig } from '../tarif-unified/types';
import { useGlobalForm } from '@workspace/core-ts/contexts';
import { cn, normalizeEuro } from '@workspace/utils';
import DOMPurify from 'dompurify';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useContent } from '../../hooks';

// Types génériques pour la configuration
export interface TarifStepFormule {
    id: string;
    nom: string;
    description: string;
    recommande: boolean;
    tarif: {
        mensuel: number;
        annuel: number;
        mensuelPromo?: number;
        annuelPromo?: number;
    };
    garanties: any[];
}

export interface TarifStepGarantie {
    code: string;
    nom?: string;
    description: string;
    badge?: string;
    plafonds?: Array<{
        label: string;
        montant?: string;
        detail?: string;
    }>;
    details?: string[];
}

export interface TarifStepOption {
    code: string;
    nom: string;
    description: string;
    tarif: {
        mensuel: number;
        annuel: number;
        mensuelPromo?: number;
        annuelPromo?: number;
    };
    avantages?: Record<string, string>;
    exclusiveToFormule?: string;
}

export interface TarifStepTextes {
    page: {
        titre: string | { site?: string; conseiller?: string; courtier?: string };
        sousTitre: string | { site?: string; conseiller?: string; courtier?: string };
    };
    formule: {
        recommande: string;
        selectionne: string;
        selectionner: string;
        renforcee?: string;
    };
    unites: {
        mois: string;
        an: string;
        soit: string;
        dont?: string;
    };
    option: {
        capitalInvalidite?: string;
    };
    garanties?: {
        titre?: string;
    };
    documents: {
        titre: string;
    };
    recapitulatif: {
        titre: string;
    };
    navigation: {
        continuer: string;
        recevoirEmail?: string;
    };
}

export interface TarifStepConfig {
    formules: TarifStepFormule[];
    garanties: TarifStepGarantie[];
    garantiesExclues: string[];
    options?: TarifStepOption[];
    documents: Array<{
        nom: string;
        url: string;
    }>;
    textes: TarifStepTextes;
    fraisDossier?: number;
    taxeAttentat?: number;
    codePromoActif?: boolean;
}

interface TarifStepProps {
    config: TarifStepConfig;
    defaultFormule?: string;
    optionCode?: string;
    onFormuleChange?: (formule: string) => void;
    onOptionChange?: (option: string, selected: boolean) => void;
    className?: string;
    tarifVariant?: 'amo' | 'mae';
    fractionnement?: 'M' | 'A'; // 'M' = Mensuel (défaut), 'A' = Annuel uniquement
    afterOptionsSlot?: React.ReactNode;
    beforeRecapSlot?: React.ReactNode;
    commissionConfig?: CommissionConfig;
}

export function TarifStep({ config, defaultFormule, optionCode, onFormuleChange, onOptionChange, tarifVariant = 'amo', fractionnement = 'M', afterOptionsSlot, beforeRecapSlot, commissionConfig }: TarifStepProps) {
    const methods = useFormContext();
    const { actions } = useGlobalForm();
    const r = useContent();

    // Fonction pour gérer l'envoi de proposition par email
    const handleOptinRecontact = () => {
        const data = methods.getValues();
        actions.saveAndGoNextStep({ ...data, RecevoirEmail: true });
    };

    // Récupération des données du tarif
    const formuleSelectionnee = methods.watch('DemandeTarif.Police.FormuleChoisie');
    const urlDocumentInformation = methods.watch('Tarif.UrlPDFDocumentInformation');
    const listeOptions = methods.watch('DemandeTarif.ListeOptions') || [];
    const optionSelectionnee = listeOptions.find((opt: any) => opt.CodeOption === optionCode)?.Souscrite || false;

    useEffect(() => {
        if (config.formules.length > 0 && !formuleSelectionnee) {
            const formuleParDefaut = defaultFormule || config.formules[0].id;
            methods.setValue('DemandeTarif.Police.FormuleChoisie', formuleParDefaut, { shouldDirty: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config.formules, defaultFormule, formuleSelectionnee]);

    useEffect(() => {
        methods.setValue('DemandeTarif.Police.FractionnementChoisi', fractionnement);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fractionnement]);

    // Formule active
    const formuleActive = config.formules.find((f) => f.id === formuleSelectionnee);

    // Vérifier si une garantie est active dans une formule
    const isGarantieActiveInFormule = (codeGarantie: string, formuleId: string) => {
        const formule = config.formules.find((f) => f.id === formuleId);
        if (!formule || !formule.garanties) return false;

        return formule.garanties.some((g: any) => g.CodeGarantie === codeGarantie && g.Souscrite === true);
    };

    // Vérifier si une garantie est exclusive à une formule
    const isGarantieExclusive = (codeGarantie: string, targetFormule: string) => {
        const activeInTarget = isGarantieActiveInFormule(codeGarantie, targetFormule);
        const activeInOthers = config.formules.filter((f) => f.id !== targetFormule).some((f) => isGarantieActiveInFormule(codeGarantie, f.id));
        return activeInTarget && !activeInOthers;
    };

    // Obtenir les informations de garantie
    const getGarantieInfo = (code: string): TarifStepGarantie | undefined => {
        return config.garanties.find((g) => g.code === code);
    };

    // Obtenir toutes les garanties uniques
    const toutesLesGaranties = useMemo(() => {
        const garantiesMap = new Map<string, any>();

        config.formules.forEach((formule) => {
            formule.garanties.forEach((garantie: any) => {
                if (!config.garantiesExclues.includes(garantie.CodeGarantie)) {
                    const existing = garantiesMap.get(garantie.CodeGarantie);
                    if (!existing || (garantie.PrimeClient && !existing.PrimeClient)) {
                        garantiesMap.set(garantie.CodeGarantie, garantie);
                    }
                }
            });
        });

        return Array.from(garantiesMap.values()).sort((a, b) => (a.OrdreAffichage || 0) - (b.OrdreAffichage || 0));
    }, [config.formules, config.garantiesExclues]);

    // Pré-calcul des descriptions sanitizées (évite DOMPurify.sanitize dans le render loop)
    const sanitizedDescriptions = useMemo(() => {
        const map = new Map<string, string>();
        config.garanties.forEach((g) => {
            if (g.description) {
                map.set(g.code, DOMPurify.sanitize(g.description));
            }
        });
        return map;
    }, [config.garanties]);

    // Calcul du tarif de l'option
    const optionActive = config.options?.find((opt) => opt.code === optionCode);
    const optionAPIData = formuleActive?.garanties?.find((g: any) => g.CodeGarantie === optionCode && g.Disponible === 'Option');

    const handleFormuleSelection = (formuleId: string) => {
        methods.setValue('DemandeTarif.Police.FormuleChoisie', formuleId, { shouldDirty: true });
        onFormuleChange?.(formuleId);
    };

    const handleOptionSelection = () => {
        if (!optionCode) return;

        const newValue = !optionSelectionnee;
        const currentOptions = methods.watch('DemandeTarif.ListeOptions') || [];
        const optionIndex = currentOptions.findIndex((opt: any) => opt.CodeOption === optionCode);

        if (optionIndex >= 0) {
            currentOptions[optionIndex].Souscrite = newValue;
            methods.setValue('DemandeTarif.ListeOptions', currentOptions, { shouldDirty: true });
        } else if (newValue) {
            methods.setValue(
                'DemandeTarif.ListeOptions',
                [
                    ...currentOptions,
                    {
                        CodeOption: optionCode,
                        Souscrite: true,
                    },
                ],
                { shouldDirty: true },
            );
        }

        onOptionChange?.(optionCode, newValue);
    };

    return (
        <>
            <div className="text-center mb-6">
                <h1 className="text-xl sm:text-2xl text-foreground mb-2">
                    {typeof config.textes.page.titre === 'string' ? config.textes.page.titre : r(config.textes.page.titre)}
                </h1>
                <p className="text-sm text-foreground">
                    {typeof config.textes.page.sousTitre === 'string' ? config.textes.page.sousTitre : r(config.textes.page.sousTitre)}
                </p>
            </div>

            {/* Cartes des formules */}
            <div className={cn('gap-4 sm:gap-6 w-full mb-8', config.formules.length === 1 ? 'flex justify-center' : 'grid grid-cols-1 sm:grid-cols-2')}>
                {config.formules.map((formule) => {
                    const garantiesForFormule = toutesLesGaranties.map((garantieAPI) => ({
                        code: garantieAPI.CodeGarantie,
                        nom: garantieAPI.LibelleGarantie,
                        active: isGarantieActiveInFormule(garantieAPI.CodeGarantie, formule.id),
                    }));

                    return (
                        <div
                            key={formule.id}
                            className={config.formules.length === 1 ? 'max-w-md w-full' : ''}
                        >
                            <PricingCard
                                id={formule.id}
                                titre={formule.nom}
                                description={formule.description}
                                prix={{
                                    valeur: fractionnement === 'A'
                                        ? (config.codePromoActif && formule.tarif.annuelPromo ? formule.tarif.annuelPromo : formule.tarif.annuel)
                                        : (config.codePromoActif && formule.tarif.mensuelPromo ? formule.tarif.mensuelPromo : formule.tarif.mensuel),
                                    periode: fractionnement === 'A' ? config.textes.unites.an : config.textes.unites.mois,
                                    sous: fractionnement === 'A'
                                        ? undefined
                                        : `${config.textes.unites.soit} ${normalizeEuro(config.codePromoActif && formule.tarif.annuelPromo ? formule.tarif.annuelPromo : formule.tarif.annuel)} / ${config.textes.unites.an}`,
                                    prixOriginal: fractionnement === 'A'
                                        ? (config.codePromoActif && formule.tarif.annuelPromo ? formule.tarif.annuel : undefined)
                                        : (config.codePromoActif && formule.tarif.mensuelPromo ? formule.tarif.mensuel : undefined),
                                }}
                                selected={formuleSelectionnee === formule.id}
                                recommande={formule.recommande}
                                onSelect={handleFormuleSelection}
                                tarifVariant={tarifVariant}
                                textes={config.textes.formule}
                            >
                                <GarantiesList garanties={garantiesForFormule} inverse={formuleSelectionnee === formule.id} />
                            </PricingCard>
                        </div>
                    );
                })}
            </div>

            {/* Détail des garanties */}
            {formuleSelectionnee && (
                <div className="w-full pt-8 space-y-3 sm:space-y-4">
                    {config.textes.garanties?.titre && (
                        <h2 className="text-base sm:text-lg font-semibold text-center mb-4">{config.textes.garanties.titre}</h2>
                    )}
                    {toutesLesGaranties.map((garantieAPI) => {
                        const active = isGarantieActiveInFormule(garantieAPI.CodeGarantie, formuleSelectionnee);
                        const configStatique = getGarantieInfo(garantieAPI.CodeGarantie);
                        const isExclusive = config.formules.length > 1 && isGarantieExclusive(garantieAPI.CodeGarantie, formuleSelectionnee);

                        return (
                            <div
                                key={garantieAPI.CodeGarantie}
                                className={cn(
                                    'rounded-lg border-2 bg-card border overflow-hidden transition-all',
                                    active ? (isExclusive ? 'border-primary' : 'border-transparent') : 'border-neutral-100 opacity-60',
                                    !active && isExclusive && 'cursor-pointer hover:opacity-80',
                                )}
                            >
                                <div className="px-4 sm:px-6 py-3 sm:py-4">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                <h3 className={cn('text-sm sm:text-base font-medium', active ? 'text-primary' : 'text-muted-foreground')}>
                                                    {garantieAPI.LibelleGarantie}
                                                </h3>
                                                {configStatique?.badge && (
                                                    <span className="px-2 py-0.5 text-xs font-semibold bg-primary text-primary-foreground rounded">
                                                        {configStatique.badge}
                                                    </span>
                                                )}
                                                {active && isExclusive && (
                                                    <span className="px-2 py-0.5 text-xs font-semibold bg-secondary text-primary">
                                                        {config.textes.formule.renforcee || 'Renforcée'}
                                                    </span>
                                                )}
                                            </div>
                                            <p
                                                className={cn('text-xs mt-1', active ? 'text-muted-foreground' : 'text-muted-foreground/60')}
                                                dangerouslySetInnerHTML={{ __html: sanitizedDescriptions.get(garantieAPI.CodeGarantie) || '' }}
                                            />
                                        </div>
                                        {active ? (
                                            <CheckIcon className="w-5 h-5 text-secondary flex-shrink-0 ml-3" />
                                        ) : (
                                            <XMarkIcon className="w-5 h-5 text-neutral-300 flex-shrink-0 ml-3" />
                                        )}
                                    </div>
                                    {active && configStatique && (configStatique.plafonds || configStatique.details) && (
                                        <div className="mt-3 pt-3 border-t border-neutral-100">
                                            {configStatique.plafonds?.map((plafond, i) =>
                                                plafond.montant || plafond.detail ? (
                                                    <div
                                                        key={i}
                                                        className="flex justify-between items-start mb-2"
                                                    >
                                                        <dt className="text-xs text-muted-foreground">{plafond.label}</dt>
                                                        <dd className="text-right ml-4">
                                                            {plafond.montant && <div className="text-xs font-medium">{plafond.montant}</div>}
                                                            {plafond.detail && (
                                                                <div className="text-xs text-neutral-500 mt-0.5 whitespace-pre-line">{plafond.detail}</div>
                                                            )}
                                                        </dd>
                                                    </div>
                                                ) : (
                                                    <p key={i} className="text-xs text-muted-foreground mb-2">
                                                        {plafond.label}
                                                    </p>
                                                ),
                                            )}
                                            {configStatique.details?.map((detail, i) => (
                                                <div
                                                    key={i}
                                                    className="flex items-start gap-2"
                                                >
                                                    <span className="w-1.5 h-1.5 bg-secondary rounded-full mt-1.5 flex-shrink-0" />
                                                    <span className="text-xs text-muted-foreground">{detail}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Option */}
            {formuleSelectionnee && optionActive && (
                <div className="w-full py-8">
                    <div className="max-w-md mx-auto">
                        {optionAPIData ? (
                            <PricingCard
                                id={`option-${optionCode}`}
                                titre={optionAPIData.LibelleGarantie}
                                description={optionActive.description}
                                prix={{
                                    valeur: fractionnement === 'A'
                                        ? (optionAPIData.PrimeClient || 0)
                                        : (optionAPIData.PrimeClientMensuelle || 0),
                                    periode: fractionnement === 'A' ? config.textes.unites.an : config.textes.unites.mois,
                                }}
                                selected={optionSelectionnee}
                                recommande
                                onSelect={handleOptionSelection}
                                variant="option"
                                tarifVariant={tarifVariant}
                                textes={config.textes.formule}
                            >
                                {optionActive.avantages && (
                                    <div className="text-center">
                                        <p className="text-xs sm:text-sm font-medium mb-2">{config.textes.option.capitalInvalidite}</p>
                                        {!optionActive.avantages.capitalAvant && (
                                            <p className="text-xs opacity-50 mb-1 sm:hidden">Jusqu&apos;à</p>
                                        )}
                                        <div className="flex items-center justify-center gap-2">
                                            {optionActive.avantages.capitalAvant && (
                                                <>
                                                    <span className="text-xs line-through opacity-50">{optionActive.avantages.capitalAvant}</span>
                                                    <span className="text-xs">→</span>
                                                </>
                                            )}
                                            {!optionActive.avantages.capitalAvant && (
                                                <span className="hidden sm:inline text-xs opacity-50">Jusqu&apos;à</span>
                                            )}
                                            <span className="text-sm font-bold">{optionActive.avantages.capitalApres}</span>
                                        </div>
                                        {optionActive.avantages.texteSecondaire && (
                                            <p className="text-xs opacity-60 mt-1">
                                                {optionActive.avantages.texteSecondaire}
                                            </p>
                                        )}
                                        {optionActive.avantages.fraisObseques && !optionActive.avantages.texteSecondaire && (
                                            <p className="text-xs opacity-60 mt-1">
                                                {config.textes.unites.dont} {optionActive.avantages.fraisObseques} frais obsèques
                                            </p>
                                        )}
                                    </div>
                                )}
                            </PricingCard>
                        ) : (
                            // Option non disponible - afficher avec bouton pour changer de formule
                            <div className="bg-background p-4 sm:p-6 text-center border-2 border-border">
                                <div className="mb-4">
                                    <h3 className="text-base sm:text-lg font-semibold text-muted-foreground mb-1">{optionActive.nom}</h3>
                                    <p className="text-xs text-muted-foreground">{optionActive.description}</p>
                                </div>

                                <div className="mb-4">
                                    <div className="flex items-baseline justify-center gap-1 mb-3">
                                        <span className="text-2xl sm:text-3xl font-bold text-muted-foreground">
                                            +{normalizeEuro(fractionnement === 'A' ? optionActive.tarif.annuel : optionActive.tarif.mensuel)}
                                        </span>
                                        <span className="text-sm text-muted-foreground">/ {fractionnement === 'A' ? config.textes.unites.an : config.textes.unites.mois}</span>
                                    </div>

                                    {optionActive.avantages && (
                                        <div className="text-center mb-4">
                                            <p className="text-xs sm:text-sm font-medium mb-2 text-muted-foreground">
                                                {config.textes.option.capitalInvalidite}
                                            </p>
                                            {!optionActive.avantages.capitalAvant && (
                                                <p className="text-xs text-muted-foreground opacity-50 mb-1 sm:hidden">Jusqu&apos;à</p>
                                            )}
                                            <div className="flex items-center justify-center gap-2">
                                                {optionActive.avantages.capitalAvant && (
                                                    <>
                                                        <span className="text-xs line-through opacity-50 text-muted-foreground">
                                                            {optionActive.avantages.capitalAvant}
                                                        </span>
                                                        <span className="text-xs text-muted-foreground">→</span>
                                                    </>
                                                )}
                                                {!optionActive.avantages.capitalAvant && (
                                                    <span className="hidden sm:inline text-xs text-muted-foreground opacity-50">Jusqu&apos;à</span>
                                                )}
                                                <span className="text-sm font-bold text-foreground">{optionActive.avantages.capitalApres}</span>
                                            </div>
                                            {optionActive.avantages.texteSecondaire && (
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    {optionActive.avantages.texteSecondaire}
                                                </p>
                                            )}
                                            {optionActive.avantages.fraisObseques && !optionActive.avantages.texteSecondaire && (
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    {config.textes.unites.dont} {optionActive.avantages.fraisObseques} frais obsèques
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Bouton pour changer vers une formule qui supporte l'option */}
                                {optionActive.exclusiveToFormule && (
                                    <Button
                                        type="button"
                                        variant="primary"
                                        size="sm"
                                        onClick={() => handleFormuleSelection(optionActive.exclusiveToFormule!)}
                                    >
                                        Passer à la formule {config.formules.find((f) => f.id === optionActive.exclusiveToFormule)?.nom || 'Renforcée'}
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Slot personnalisé après les options (ex: bon à savoir) */}
            {afterOptionsSlot}

            {/* Documents */}
            <DocumentsList
                documents={config.documents}
                textes={config.textes.documents}
            />
            {tarifVariant === 'amo' && <Commission config={commissionConfig} />}

            {/* Slot personnalisé avant le récapitulatif (ex: code promo) */}
            {beforeRecapSlot}

            {/* Récapitulatif */}
            {formuleSelectionnee && (
                <TarifRecap
                    formule={{
                        nom: formuleActive?.nom || '',
                        tarif: fractionnement === 'A'
                            ? (config.codePromoActif && formuleActive?.tarif.annuelPromo
                                ? formuleActive.tarif.annuelPromo
                                : formuleActive?.tarif.annuel || 0)
                            : (config.codePromoActif && formuleActive?.tarif.mensuelPromo
                                ? formuleActive.tarif.mensuelPromo
                                : formuleActive?.tarif.mensuel || 0),
                        tarifOriginal: fractionnement === 'A'
                            ? (config.codePromoActif && formuleActive?.tarif.annuelPromo
                                ? formuleActive.tarif.annuel
                                : undefined)
                            : (config.codePromoActif && formuleActive?.tarif.mensuelPromo
                                ? formuleActive.tarif.mensuel
                                : undefined),
                    }}
                    options={
                        optionSelectionnee && optionAPIData
                            ? [
                                  {
                                      nom: `Option ${optionAPIData.LibelleGarantie}`,
                                      tarif: fractionnement === 'A'
                                          ? (optionAPIData.PrimeClient || 0)
                                          : (optionAPIData.PrimeClientMensuelle || 0),
                                  },
                              ]
                            : []
                    }
                    fraisDossier={config.fraisDossier}
                    taxeAttentat={config.taxeAttentat}
                    fractionnement={fractionnement}
                    textes={config.textes.recapitulatif}
                />
            )}

            {formuleSelectionnee && (
                <div className="flex flex-col items-center gap-3">
                    <SmartButton
                        value={config.textes.navigation.continuer}
                        className="min-w-[150px] sm:min-w-[200px]"
                    />
                    <button
                        type="button"
                        onClick={handleOptinRecontact}
                        className="text-sm text-muted-foreground hover:text-primary underline transition-colors"
                    >
                        {config.textes.navigation.recevoirEmail}
                    </button>
                    {urlDocumentInformation && (
                        <p className="text-xs text-center">
                            <a
                                href={urlDocumentInformation}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary underline hover:no-underline"
                            >
                                Veuillez trouver ci-joint le document d’information sur le produit d’assurance proposé.
                            </a>
                        </p>
                    )}
                </div>
            )}
        </>
    );
}
